
import React, { useState } from 'react';
import { DvprManager } from '../../components/DvprManager';
import { AltAnalysis } from '../../components/AltAnalysis';
import { Reporting } from '../../components/Reporting';
import { LimsModule } from '../../components/LimsModule';
import { Dashboard as ExternalDashboard } from '../../components/Dashboard';
import { DvprItem, TestStatus, AltDataPoint, LabTestRequest, LabRig, LabSample, LabRequestStatus, RigStatus } from '../../types';

// Initial Mock Data
const INITIAL_DVPR_ITEMS: DvprItem[] = [
  { id: 'REQ-001', requirement: 'Operating Temperature Range', specSection: '3.1.1', testMethod: 'Thermal Cycling', sampleSize: 10, duration: '500 Cycles', status: TestStatus.IN_PROGRESS, failures: 0, comments: 'Running smoothly at cycle 350.' },
  { id: 'REQ-002', requirement: 'Vibration Resistance', specSection: '3.2.4', testMethod: 'Random Vibration', sampleSize: 6, duration: '24 Hours', status: TestStatus.COMPLETED, failures: 0, comments: 'Passed per ISO 16750-3.' },
  { id: 'REQ-003', requirement: 'Overvoltage Protection', specSection: '4.1.2', testMethod: 'Electrical Stress', sampleSize: 5, duration: 'N/A', status: TestStatus.FAILED, failures: 2, comments: 'MOSFET failure observed at 32V input.' },
  { id: 'REQ-004', requirement: 'Humidity Resistance', specSection: '3.1.5', testMethod: '85/85 Test', sampleSize: 10, duration: '1000 Hours', status: TestStatus.PLANNED, failures: 0, comments: 'Scheduled for next week.' },
  { id: 'REQ-005', requirement: 'Button Life', specSection: '5.2.1', testMethod: 'Pneumatic Cycling', sampleSize: 20, duration: '1M Cycles', status: TestStatus.COMPLETED, failures: 0, comments: 'No mechanical degradation.' },
];

const INITIAL_ALT_DATA: AltDataPoint[] = [
  { id: '1', stressLevel: 55, cyclesToFailure: 15000, censored: true },
  { id: '2', stressLevel: 55, cyclesToFailure: 18000, censored: true },
  { id: '3', stressLevel: 70, cyclesToFailure: 8500, censored: false },
  { id: '4', stressLevel: 70, cyclesToFailure: 9200, censored: false },
  { id: '5', stressLevel: 85, cyclesToFailure: 3200, censored: false },
  { id: '6', stressLevel: 85, cyclesToFailure: 2800, censored: false },
  { id: '7', stressLevel: 100, cyclesToFailure: 1100, censored: false },
  { id: '8', stressLevel: 100, cyclesToFailure: 950, censored: false },
];

// LIMS Mocks
const INITIAL_REQUESTS: LabTestRequest[] = [
  { 
    id: 'LR-2025-001', 
    projectCode: 'PRJ-2025-A',
    customer: 'Automotive OEM A',
    partDetails: { name: 'ECU-Main-Board', batch: 'B2025-01', dateCode: '2501', qty: 10 },
    testName: 'Thermal Cycle - Profile A', 
    requestor: 'J. Smith', 
    priority: 'Normal', 
    status: LabRequestStatus.IN_PROGRESS, 
    requestDate: '2025-02-10', 
    dueDate: '2025-02-25',
    samples: ['S-104', 'S-105'],
    assignedRigId: 'RIG-001',
    testParameters: { tempLow: -40, tempHigh: 85, cycles: 500 },
    qcGates: { calibrationChecked: true, samplesVerified: true, environmentChecked: false } 
  },
  { 
    id: 'LR-2025-002', 
    projectCode: 'PRJ-2025-B',
    customer: 'Energy Systems Inc.',
    partDetails: { name: 'Inv-Power-Mod', batch: 'B2025-02', dateCode: '2502', qty: 6 },
    testName: 'Vibration - Random Z-Axis', 
    requestor: 'M. Garcia', 
    priority: 'High', 
    status: LabRequestStatus.SUBMITTED, 
    requestDate: '2025-02-12', 
    dueDate: '2025-02-15',
    samples: ['S-201', 'S-202'],
    testParameters: { axis: 'Z', duration: 24 },
    qcGates: { calibrationChecked: false, samplesVerified: false, environmentChecked: false } 
  },
  { 
    id: 'LR-2025-003', 
    projectCode: 'PRJ-2025-C',
    customer: 'Tier 1 Supplier',
    partDetails: { name: 'Conn-Housing-Main', batch: 'B2025-03', dateCode: '2503', qty: 12 },
    testName: 'Salt Fog Exposure', 
    requestor: 'A. Chen', 
    priority: 'Normal', 
    status: LabRequestStatus.APPROVED, 
    requestDate: '2025-02-11', 
    dueDate: '2025-03-01',
    samples: ['S-301'],
    testParameters: { duration: 96, temp: 35 },
    qcGates: { calibrationChecked: false, samplesVerified: false, environmentChecked: false } 
  }
];

const INITIAL_RIGS: LabRig[] = [
  { id: 'RIG-001', name: 'Thermal Chamber A', type: 'Environmental Chamber', status: RigStatus.IN_USE, calibrationDue: '2025-06-15', location: 'Lab 1 - Row A', currentTestId: 'LR-2025-001' },
  { id: 'RIG-002', name: 'Vibration Table 1', type: 'Shaker Table', status: RigStatus.AVAILABLE, calibrationDue: '2025-03-01', location: 'Lab 2 - Bay 1' },
  { id: 'RIG-003', name: 'Oven B', type: 'High Temp Oven', status: RigStatus.AVAILABLE, calibrationDue: '2024-12-01', location: 'Lab 1 - Row B' }, // Overdue
  { id: 'RIG-004', name: 'Drop Tower', type: 'Mechanical', status: RigStatus.MAINTENANCE, calibrationDue: '2025-08-20', location: 'Lab 3' },
];

const INITIAL_SAMPLES: LabSample[] = [
  { id: 'S-104', name: 'ECU Rev 2', batch: 'B2025-01', status: 'In Testing', location: 'RIG-001' },
  { id: 'S-201', name: 'Connector Housing', batch: 'B2025-02', status: 'Received', location: 'Storage A' },
];

const ExternalAppPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dvprItems, setDvprItems] = useState<DvprItem[]>(INITIAL_DVPR_ITEMS);
  const [altData, setAltData] = useState<AltDataPoint[]>(INITIAL_ALT_DATA);

  // LIMS State
  const [labRequests, setLabRequests] = useState<LabTestRequest[]>(INITIAL_REQUESTS);
  const [labRigs, setLabRigs] = useState<LabRig[]>(INITIAL_RIGS);
  const [labSamples, setLabSamples] = useState<LabSample[]>(INITIAL_SAMPLES);

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <ExternalDashboard dvprItems={dvprItems} />;
      case 'lims':
        return (
          <LimsModule 
            requests={labRequests} 
            setRequests={setLabRequests}
            rigs={labRigs}
            setRigs={setLabRigs}
            samples={labSamples}
            setSamples={setLabSamples}
          />
        );
      case 'dvpr':
        return <DvprManager items={dvprItems} setItems={setDvprItems} />;
      case 'alt':
        return <AltAnalysis data={altData} setData={setAltData} />;
      case 'reports':
        return <Reporting dvprItems={dvprItems} altData={altData} />;
      default:
        return (
          <div className="flex flex-col items-center justify-center h-full text-slate-400">
            <h3 className="text-xl font-medium">Coming Soon</h3>
            <p>This module is currently under development.</p>
          </div>
        );
    }
  };

  return (
    <div className="flex flex-col h-full bg-slate-50 font-sans text-slate-900" style={{ minHeight: '80vh' }}>
      <div className="flex border-b border-gray-200 bg-white">
        <button
            onClick={() => setActiveTab('dashboard')}
            className={`px-4 py-2 ${activeTab === 'dashboard' ? 'border-b-2 border-blue-500 font-bold' : ''}`}
        >
            Dashboard
        </button>
        <button
            onClick={() => setActiveTab('lims')}
            className={`px-4 py-2 ${activeTab === 'lims' ? 'border-b-2 border-blue-500 font-bold' : ''}`}
        >
            LIMS
        </button>
        <button
            onClick={() => setActiveTab('dvpr')}
            className={`px-4 py-2 ${activeTab === 'dvpr' ? 'border-b-2 border-blue-500 font-bold' : ''}`}
        >
            DVP&R
        </button>
        <button
            onClick={() => setActiveTab('alt')}
            className={`px-4 py-2 ${activeTab === 'alt' ? 'border-b-2 border-blue-500 font-bold' : ''}`}
        >
            ALT Analysis
        </button>
        <button
            onClick={() => setActiveTab('reports')}
            className={`px-4 py-2 ${activeTab === 'reports' ? 'border-b-2 border-blue-500 font-bold' : ''}`}
        >
            Reports
        </button>
      </div>

      <div className="flex-1 overflow-auto p-6 lg:p-8 custom-scrollbar relative">
        {renderContent()}
      </div>
    </div>
  );
};

export default ExternalAppPage;
